import { useState } from 'react';
import { Download } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { api, downloadFile } from '../../lib/api.js';
import { useListQuery } from '../../hooks/useListQuery.js';
import { useDebouncedValue } from '../../hooks/useDebouncedValue.js';
import { DataTable } from '../../components/ui/DataTable.jsx';
import { Button } from '../../components/ui/Button.jsx';

const COLUMNS = [
  {
    key: 'created_at',
    header: 'When',
    sortKey: 'created_at',
    render: (r) => new Date(r.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }),
  },
  { key: 'userName', header: 'User', render: (r) => r.userName ?? 'System' },
  { key: 'action', header: 'Action', sortKey: 'action' },
  { key: 'summary', header: 'Summary', render: (r) => r.summary ?? '—' },
];

export default function AuditLogPage() {
  const [query, onQueryChange] = useListQuery({ sort: 'created_at' });
  const debouncedSearch = useDebouncedValue(query.search, 350);
  const [downloading, setDownloading] = useState(false);

  const listQuery = useQuery({
    queryKey: ['reports', 'audit-log', { ...query, search: debouncedSearch }],
    queryFn: () => api.get('/reports/audit-log', { query: { ...query, search: debouncedSearch } }),
    placeholderData: (prev) => prev,
  });

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadFile('/reports/exports/audit-log', 'audit-log.csv', { query: { search: debouncedSearch } });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight">Audit log</h1>
          <p className="mt-1 text-sm text-muted">Every create, update, cancel and rate entry, with who and when.</p>
        </div>
        <Button variant="secondary" size="sm" loading={downloading} onClick={handleDownload}>
          <Download size={16} aria-hidden="true" /> CSV
        </Button>
      </div>

      <DataTable
        columns={COLUMNS}
        data={listQuery.data?.data ?? []}
        meta={listQuery.data?.meta}
        query={query}
        onQueryChange={onQueryChange}
        isLoading={listQuery.isLoading}
        searchPlaceholder="Search by summary or action…"
        emptyLabel="No activity recorded yet."
      />
    </div>
  );
}
